import { PaginatedResponse, useApi } from '@/api';
import { HyperLiquidLink, Link, TableCellBottom } from '@/components';
import { DEFAULT_NETWORK } from '@/constants';
import {
  CurrencyValue,
  DataTable,
  DropdownFilter,
  Flex,
  RelativeDateTime,
  TableSkeleton,
  TextSearchFilter,
  useTableProps,
  WalletAddress
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

type Liquidation = {
  datetime: string;
  tx_hash: string;
  debt_underlying_address: string;
  collateral_underlying_address: string;
  wallet_address: string;
  liquidator_address: string;
  debt_underlying_symbol: string;
  debt_repaid: string;
  debt_repaid_usd: string;
  collateral_underlying_symbol: string;
  collateral_seized: string;
  collateral_seized_usd: string;
  liquidation_bonus_usd: string;
  order_index: string;
};

type LiquidationsData = Liquidation;

export default function LiquidationsTable() {
  const network = DEFAULT_NETWORK;

  const { pagination, sorting, filters } = useTableProps({
    pageSize: 10,
    initialSorting: '-order_index',
    filtersDebounceTime: 500,
    initialFilters: {
      textSearch: '',
      selectedCollateralOption: '',
      selectedDebtOption: ''
    }
  });
  const [data, error] = useApi<PaginatedResponse<LiquidationsData[]>>({
    url: '/liquidations/',
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting,
      search: filters.filtersApplied.textSearch,

      collateral_assets:
        filters.filtersApplied.selectedCollateralOption.toString(),
      debt_assets: filters.filtersApplied.selectedDebtOption.toString()
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }
  const columns: TableColumn[] = [
    {
      header: 'Wallet',
      getCell: (row) => (
        <Link
          key={row.wallet_address}
          to={`../wallets/${row.wallet_address}`}
          relative="path">
          <WalletAddress address={row.wallet_address} />
        </Link>
      )
    },
    {
      header: 'Collateral Liquidated',
      getCell: (row) => (
        <CurrencyValue
          value={row.collateral_seized}
          options={{
            currency: {
              name: row.collateral_underlying_symbol,
              address: row.collateral_underlying_address,
              network: network
            }
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.collateral_seized_usd}>
          <CurrencyValue
            value={row.collateral_seized_usd}
            options={{
              currency: 'USD'
            }}
          />
        </TableCellBottom>
      ),
      sortingField: 'collateral_seized_usd',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Debt Repaid',
      getCell: (row) => (
        <CurrencyValue
          value={row.debt_repaid}
          options={{
            currency: {
              name: row.debt_underlying_symbol,
              address: row.debt_underlying_address,
              network: network
            }
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.debt_repaid_usd}>
          <CurrencyValue
            value={row.debt_repaid_usd}
            options={{
              currency: 'USD'
            }}
          />
        </TableCellBottom>
      ),
      sortingField: 'debt_repaid_usd',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Liquidator',
      getCell: (row) => (
        <Link
          key={row.liquidator_address}
          to={`./liquidators/${row.liquidator_address}`}
          relative="path">
          <WalletAddress address={row.liquidator_address} />
        </Link>
      )
    },
    {
      header: 'Liquidation Bonus',
      getCell: (row) => (
        <CurrencyValue
          value={row.liquidation_bonus_usd}
          options={{
            currency: 'USD'
          }}
        />
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'liquidation_bonus_usd'
    },
    {
      header: 'Time',
      getCell: (row) => (
        <Flex gap="xs" color="muted">
          <RelativeDateTime value={row.datetime} />
          <HyperLiquidLink path={`tx/${row.tx_hash}`} />
        </Flex>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'datetime'
    }
  ];

  return (
    <Flex gap="m" flexDirection="column" justifyContent="flex-start">
      <Flex
        flexDirection={['column', 'row']}
        gap={['xs', 's']}
        justifyContent={['center', 'flex-end']}
        alignItems="center">
        <Flex flexDirection="row" gap={['xs', 's']}>
          <DropdownFilter
            multi={true}
            withCryptoIcon={true}
            filterField="selectedCollateralOption"
            options={data.extra?.collateral_assets || []}
            pagination={pagination}
            titleFormat={(filteredCount: number) =>
              `Collateral asset (${filteredCount})`
            }
            {...filters}
          />
          <DropdownFilter
            multi={true}
            withCryptoIcon={true}
            filterField="selectedDebtOption"
            options={data.extra?.debt_assets || []}
            pagination={pagination}
            titleFormat={(filteredCount) => `Debt asset (${filteredCount})`}
            {...filters}
          />
        </Flex>
        <Flex flexDirection="row">
          <TextSearchFilter
            pagination={pagination}
            placeholder="Search by wallet"
            {...filters}
          />
        </Flex>
      </Flex>
      <DataTable
        rows={data.results}
        columns={columns}
        getKey={(row) => `${row.order_index}`}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.pagination.total
        }}
        width="100%"
      />
    </Flex>
  );
}
